import { useState, useEffect } from 'react';
import api, { downloadCsv } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import Badge from './Badge';
import StateBlock from './StateBlock';
import EntityLink from './EntityLink';

// Bank Reconciliation → PNC Payments (2026-10-14): import PNC payment-report
// PDFs, and the read-only reconciliation of those payments against AP
// payments. Fixes are never made here - only "explained" notes on a bank
// payment (e.g. paid to the wrong payee and refunded).
const FLAG_VARIANT = {
  SUM_MISMATCH: 'warning', DUPLICATE_LINK: 'danger', DELETED_BANK_PAYMENT: 'danger',
  NO_BANK_MATCH: 'warning', BANK_ONLY: 'info', MEMO_REF_MISMATCH: 'neutral',
};
const inputStyle = { padding: '6px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13 };
const labelStyle = { fontSize: 11, color: 'var(--muted)', fontWeight: 600 };
const EMPTY_FILTERS = { vendor_id: '', contractor_id: '', from_date: '', to_date: '', flag: 'any', category: '' };
const PAGE = 200;

function fmt(n) {
  if (n === null || n === undefined) return '—';
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function PncPaymentsTab() {
  const { user } = useAuth();
  const canWrite = ['admin', 'manager'].includes(user?.role);
  const [files, setFiles] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState(null);
  const [options, setOptions] = useState({ vendors: [], contractors: [], reports: [] });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [recon, setRecon] = useState(null);
  const [error, setError] = useState(false);
  const [shown, setShown] = useState(PAGE);
  const [explaining, setExplaining] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkCategory, setBulkCategory] = useState('payroll_direct');

  function loadOptions() { api.get('/bank-payments/filters').then(r => setOptions(r.data)).catch(() => {}); }
  function load(f = filters) {
    setError(false);
    setRecon(null);
    setShown(PAGE);
    setSelected(new Set());
    api.get('/bank-payments/recon', { params: clean(f) }).then(r => setRecon(r.data)).catch(() => setError(true));
  }
  useEffect(() => { loadOptions(); load(EMPTY_FILTERS); }, []);

  function clean(f) { return Object.fromEntries(Object.entries(f).filter(([, v]) => v)); }
  function formData() {
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));
    return fd;
  }
  const errText = (e, fallback) => e.response?.data?.error || fallback;

  async function runPreview() {
    setBusy('preview'); setMessage(null); setPreview(null);
    try {
      const r = await api.post('/bank-payments/preview', formData(), { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(r.data.files);
    } catch (e) { setMessage({ type: 'error', text: errText(e, 'Preview failed') }); }
    finally { setBusy(''); }
  }

  async function runCommit() {
    setBusy('commit'); setMessage(null);
    try {
      const r = await api.post('/bank-payments/commit', formData(), { headers: { 'Content-Type': 'multipart/form-data' } });
      const ins = r.data.files.reduce((s, f) => s + f.inserted, 0);
      const upd = r.data.files.reduce((s, f) => s + f.updated, 0);
      const same = r.data.files.reduce((s, f) => s + f.same, 0);
      setMessage({ type: 'info', text: `Imported ${r.data.files.length} report(s): ${ins} new, ${upd} changed, ${same} already present. AP payments linked: ${r.data.link.linked} (${r.data.link.changed} link(s) set or changed${r.data.link.ambiguous.length ? `, ${r.data.link.ambiguous.length} ambiguous left unlinked` : ''}).` });
      setPreview(null); setFiles([]);
      loadOptions(); load();
    } catch (e) { setMessage({ type: 'error', text: errText(e, 'Import failed') }); }
    finally { setBusy(''); }
  }

  async function relink() {
    setBusy('relink'); setMessage(null);
    try {
      const r = await api.post('/bank-payments/relink');
      setMessage({ type: 'info', text: `Re-linked: ${r.data.changed} link(s) set or changed; ${r.data.linked} AP payments linked${r.data.ambiguous.length ? `; ${r.data.ambiguous.length} ambiguous left unlinked` : ''}.` });
      load();
    } catch (e) { setMessage({ type: 'error', text: errText(e, 'Re-link failed') }); }
    finally { setBusy(''); }
  }

  async function saveExplanation() {
    try {
      await api.post(`/bank-payments/${explaining.id}/explain`, { note: explaining.note });
      setExplaining(null);
      load();
    } catch (e) { setMessage({ type: 'error', text: errText(e, 'Could not save the explanation') }); }
  }

  async function applyBulkCategory() {
    setBusy('bulk'); setMessage(null);
    try {
      const r = await api.post('/bank-payments/bulk-category', { ids: [...selected], category: bulkCategory });
      setMessage({ type: 'info', text: `Category set on ${r.data.updated} payment(s)${r.data.skipped ? ` (${r.data.skipped} unchanged or linked to AP, skipped)` : ''}.` });
      load();
    } catch (e) { setMessage({ type: 'error', text: errText(e, 'Could not set the category') }); }
    finally { setBusy(''); }
  }
  function toggle(id) {
    setSelected(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }

  async function viewPdf(documentId) {
    try {
      const r = await api.get(`/documents/${documentId}/view`, { responseType: 'blob' });
      window.open(window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank');
    } catch (e) { setMessage({ type: 'error', text: 'Could not open the PNC report' }); }
  }

  const changedItems = (preview || []).flatMap(f => (f.items || []).filter(i => i.state === 'changed').map(i => ({ ...i, file_name: f.file_name })));
  const rows = recon?.rows || [];
  const selectable = rows.filter(r => r.kind === 'bank' && !r.ap_payments.length && !(r.advances || []).length && r.status === 'Processed');
  const catLabel = k => recon?.category_labels?.[k] || k;

  return (
    <div>
      {/* Import */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 12, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <strong style={{ fontSize: 13 }}>Import PNC payment reports</strong>
          <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
            {files.length ? `${files.length} PDF(s) selected` : 'Choose PDFs…'}
            <input type="file" accept=".pdf" multiple style={{ display: 'none' }}
              onChange={e => { setFiles([...e.target.files]); setPreview(null); setMessage(null); e.target.value = ''; }} />
          </label>
          <button className="btn btn-primary" disabled={!files.length || !!busy} onClick={runPreview}>{busy === 'preview' ? 'Reading…' : 'Preview'}</button>
          {preview && canWrite && (
            <button className="btn btn-primary" disabled={!!busy || !preview.some(f => !f.error && (f.new || f.changed || !f.document_id))} onClick={runCommit}>
              {busy === 'commit' ? 'Importing…' : 'Commit import'}
            </button>
          )}
          <span className="form-hint">Pinacle → Payments → Payment report (PDF). Re-importing is safe: payments are matched on trace ID.</span>
        </div>
        {preview && (
          <div style={{ marginTop: 12 }}>
            <table className="data-table">
              <thead><tr><th>File</th><th>Period</th><th style={{ textAlign: 'right' }}>Payments</th><th style={{ textAlign: 'right' }}>Total</th><th style={{ textAlign: 'right' }}>New</th><th style={{ textAlign: 'right' }}>Already present</th><th style={{ textAlign: 'right' }}>Changed</th><th style={{ textAlign: 'right' }}>Not processed</th><th>Notes</th></tr></thead>
              <tbody>
                {preview.map(f => (
                  <tr key={f.file_name}>
                    <td style={{ fontSize: 12 }}>{f.file_name}</td>
                    {f.error ? <td colSpan={8} style={{ color: 'var(--danger)' }}>{f.error}</td> : <>
                      <td style={{ fontSize: 12 }}>{f.period_from} – {f.period_to}</td>
                      <td style={{ textAlign: 'right' }}>{f.count}</td>
                      <td style={{ textAlign: 'right' }}>{fmt(f.total)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{f.new}</td>
                      <td style={{ textAlign: 'right' }}>{f.same}</td>
                      <td style={{ textAlign: 'right', color: f.changed ? 'var(--warning)' : undefined }}>{f.changed}</td>
                      <td style={{ textAlign: 'right' }}>{f.not_processed}</td>
                      <td style={{ fontSize: 11, color: 'var(--muted)' }}>
                        {f.document_id ? 'PDF already stored. ' : ''}
                        {f.warnings.length ? <span title={f.warnings.join('\n')}>{f.warnings.length} parser note(s)</span> : ''}
                      </td>
                    </>}
                  </tr>
                ))}
              </tbody>
            </table>
            {changedItems.length > 0 && (
              <div style={{ marginTop: 10, fontSize: 12 }}>
                <strong>Changed since the last import:</strong>
                {changedItems.map(i => (
                  <div key={i.trace_id} style={{ marginTop: 4 }}>
                    {i.trace_id} ({i.display_name}): {i.changed.map(k => `${k} ${i.old?.[k] ?? '—'} → ${i[k] ?? '—'}`).join('; ')}
                  </div>
                ))}
              </div>
            )}
            {!canWrite && <div className="form-hint" style={{ marginTop: 8 }}>An admin or manager commits the import.</div>}
          </div>
        )}
      </div>

      {message && (
        <div style={{ background: message.type === 'error' ? 'var(--danger-bg)' : 'var(--info-bg)', color: message.type === 'error' ? 'var(--danger-text)' : 'var(--info-text)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 16 }}>
          {message.text}
        </div>
      )}

      {/* Filters */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 12, marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 200 }}>
            <label style={labelStyle}>VENDOR</label>
            <select value={filters.vendor_id} onChange={e => setFilters(f => ({ ...f, vendor_id: e.target.value }))} style={inputStyle}>
              <option value="">All vendors</option>
              {options.vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 180 }}>
            <label style={labelStyle}>CONTRACTOR</label>
            <select value={filters.contractor_id} onChange={e => setFilters(f => ({ ...f, contractor_id: e.target.value }))} style={inputStyle}>
              <option value="">All contractors</option>
              {options.contractors.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={labelStyle}>FROM DATE</label>
            <input type="date" value={filters.from_date} onChange={e => setFilters(f => ({ ...f, from_date: e.target.value }))} style={inputStyle} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={labelStyle}>TO DATE</label>
            <input type="date" value={filters.to_date} onChange={e => setFilters(f => ({ ...f, to_date: e.target.value }))} style={inputStyle} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 200 }}>
            <label style={labelStyle}>FLAG</label>
            <select value={filters.flag} onChange={e => setFilters(f => ({ ...f, flag: e.target.value }))} style={inputStyle}>
              <option value="any">Any open flag</option>
              {Object.entries(recon?.flag_labels || {}).map(([k, label]) => <option key={k} value={k}>{k} – {label}</option>)}
              <option value="explained">Explained</option>
              <option value="not_ap">Not AP (bank-only)</option>
              <option value="">All rows</option>
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 170 }}>
            <label style={labelStyle}>BANK-ONLY CATEGORY</label>
            <select value={filters.category} onChange={e => setFilters(f => ({ ...f, category: e.target.value }))} style={inputStyle}>
              <option value="">Any</option>
              {Object.entries(recon?.category_labels || {}).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={() => load()}>Apply</button>
            <button className="btn btn-secondary" onClick={() => { setFilters(EMPTY_FILTERS); load(EMPTY_FILTERS); }}>Clear</button>
            <button className="btn btn-secondary" onClick={() => downloadCsv('/bank-payments/recon/export', `PNC_Reconciliation_${new Date().toISOString().slice(0, 10)}.csv`, clean(filters))}>⬇ CSV</button>
            {canWrite && <button className="btn btn-secondary" disabled={!!busy} onClick={relink} title="Recompute which AP payments point at which PNC payment (after trace IDs were edited)">{busy === 'relink' ? 'Re-linking…' : 'Re-link'}</button>}
          </div>
        </div>
      </div>

      {error && <StateBlock type="error" action={<button className="btn btn-primary" onClick={() => load()}>Try again</button>} />}
      {!error && !recon && <StateBlock type="loading" />}
      {recon && recon.totals.bank_payments === 0 && (
        <StateBlock type="empty" title="No PNC payments imported yet" message="Import the PNC payment-report PDFs above." />
      )}

      {recon && recon.totals.bank_payments > 0 && (
        <>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <span>{recon.totals.bank_payments} PNC payments ({recon.coverage.from} – {recon.coverage.to}) · {recon.totals.linked_ap_payments} of {recon.totals.ap_payments} AP payments linked</span>
            {Object.entries(recon.summary.flags).map(([k, n]) => (
              <span key={k} style={{ cursor: 'pointer' }} onClick={() => { const f = { ...filters, flag: k }; setFilters(f); load(f); }}>
                <Badge variant={n ? FLAG_VARIANT[k] : 'neutral'}>{k} {n}</Badge>
              </span>
            ))}
            {recon.summary.explained > 0 && <span>· {recon.summary.explained} explained</span>}
            {recon.stale_links > 0 && <strong style={{ color: 'var(--warning)' }}>{recon.stale_links} stored link(s) out of date – Re-link</strong>}
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
            Bank-only by category: {Object.entries(recon.summary.bank_only_by_category).filter(([, c]) => c.open + c.explained)
              .map(([k, c]) => `${catLabel(k)} ${c.open} open${c.explained ? `, ${c.explained} explained` : ''} (${fmt(c.amount)})`).join(' · ') || '—'}
          </div>
          <div style={{ fontSize: 12, marginBottom: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span>{rows.length} row(s)</span>
            {canWrite && selectable.length > 0 && (
              <>
                <button className="btn btn-secondary" onClick={() => setSelected(new Set(rows.slice(0, shown).filter(r => selectable.includes(r)).map(r => r.bank_payment_id)))}>
                  Select bank-only rows shown
                </button>
                {selected.size > 0 && <>
                  <strong>{selected.size} selected</strong>
                  <select value={bulkCategory} onChange={e => setBulkCategory(e.target.value)} style={inputStyle}>
                    {Object.entries(recon.category_labels).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                  </select>
                  <button className="btn btn-primary" disabled={!!busy} onClick={applyBulkCategory}>
                    {['ap_vendor', 'payroll_direct'].includes(bulkCategory) ? 'Set category' : 'Mark not AP'}
                  </button>
                  <button className="btn btn-secondary" onClick={() => setSelected(new Set())}>Clear selection</button>
                </>}
              </>
            )}
          </div>
          <table className="data-table">
            <thead>
              <tr>
                {canWrite && <th></th>}<th>Flags</th><th>Date</th><th>PNC payment / AP payment</th><th>Payee / Vendor</th>
                <th style={{ textAlign: 'right' }}>Bank</th><th style={{ textAlign: 'right' }}>AP cash</th><th style={{ textAlign: 'right' }}>Diff</th>
                <th>Invoices</th><th>Memo / note</th><th style={{ textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, shown).map(r => r.kind === 'bank' ? (
                <tr key={r.key} style={{ opacity: r.explained_note || r.auto_explained ? 0.7 : 1 }}>
                  {canWrite && <td>{selectable.includes(r) && <input type="checkbox" checked={selected.has(r.bank_payment_id)} onChange={() => toggle(r.bank_payment_id)} />}</td>}
                  <td>{r.flags.map(f => <div key={f}><Badge variant={r.explained_note || r.auto_explained ? 'neutral' : FLAG_VARIANT[f]}>{f}</Badge></div>)}</td>
                  <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{r.payment_date}</td>
                  <td style={{ fontSize: 12 }}>
                    <div style={{ fontFamily: 'monospace' }}>{r.trace_id}</div>
                    <div style={{ color: 'var(--muted)' }}>{r.pay_type} · {r.status !== 'Processed' ? <Badge variant="danger">{r.status}</Badge> : r.status}{r.internal_ref ? ` · ${r.internal_ref}` : ''}</div>
                    {r.statement && <div style={{ color: 'var(--muted)' }}>Statement {r.statement.date} · {r.statement.category}{r.statement.sub_category ? ` / ${r.statement.sub_category}` : ''}</div>}
                    {!r.statement && r.statement_note && <div style={{ color: 'var(--muted)', fontStyle: 'italic' }}>{r.statement_note}</div>}
                    {r.return_credit && <div style={{ color: 'var(--success)' }}>Credit back {r.return_credit.date} {fmt(r.return_credit.amount)}</div>}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    <div>{r.payee_name}</div>
                    {r.display_name && r.display_name !== r.payee_name && <div style={{ color: 'var(--muted)' }}>{r.display_name}</div>}
                    {[...new Set([...r.ap_payments.map(p => p.vendor_name || p.contractor_name), ...(r.advances || []).map(a => a.vendor_name || a.contractor_name)])].filter(Boolean).map(n => <div key={n} style={{ color: 'var(--muted)' }}>→ {n}</div>)}
                    {r.category && (
                      <div style={{ marginTop: 2 }}>
                        <Badge variant={r.category === 'ap_vendor' ? 'info' : 'neutral'}>{catLabel(r.category)}</Badge>
                        {!r.category_set && <span style={{ color: 'var(--muted)', fontSize: 11 }}> (auto)</span>}
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(r.bank_amount)}</td>
                  <td style={{ textAlign: 'right' }}>{r.ap_payments.length || (r.advances || []).length ? fmt(r.ap_cash) : '—'}</td>
                  <td style={{ textAlign: 'right', color: r.difference ? 'var(--danger)' : 'var(--muted)' }}>{r.difference ? fmt(r.difference) : '—'}</td>
                  <td style={{ fontSize: 12 }}>
                    {r.ap_payments.map(p => (
                      <div key={p.id}>
                        <EntityLink type="ap_invoice" id={p.ap_invoice_id}>{p.invoice_number}</EntityLink> {fmt(p.cash_amount)}
                        <span style={{ color: 'var(--muted)' }}> #{p.id}{p.link_method === 'internal_ref' ? ' (by internal ref)' : ''}</span>
                      </div>
                    ))}
                    {(r.advances || []).map(a => (
                      <div key={'a' + a.id}>
                        Advance paid {fmt(a.amount)} <span style={{ color: 'var(--muted)' }}>→ <EntityLink type="contractor" id={a.contractor_id}>{a.contractor_name}</EntityLink>{a.client_wo_number ? ' ' + a.client_wo_number : ''}</span>
                      </div>
                    ))}
                    {!r.ap_payments.length && !(r.advances || []).length && r.memo_invoices.length > 0 && (
                      <div style={{ color: 'var(--muted)' }}>Memo names: {r.memo_invoices.map(i => <span key={i.id}><EntityLink type="ap_invoice" id={i.id}>{i.invoice_number}</EntityLink> </span>)}</div>
                    )}
                    {(r.possible_ap || []).map(a => (
                      <div key={a.ap_payment_id} style={{ color: 'var(--warning)' }}>
                        Possible: AP payment #{a.ap_payment_id} on <EntityLink type="ap_invoice" id={a.ap_invoice_id}>{a.invoice_number}</EntityLink> (trace {a.trace_id || 'blank'})
                      </div>
                    ))}
                    {r.memo_missing_invoices.length > 0 && <div style={{ color: 'var(--muted)' }}>Not in memo: {r.memo_missing_invoices.join(', ')}</div>}
                  </td>
                  <td style={{ fontSize: 12, maxWidth: 320 }}>
                    <div>{r.memo || '—'}</div>
                    {r.auto_explained && !r.explained_note && <div style={{ marginTop: 4, color: 'var(--success)' }}>✓ {r.auto_explained}</div>}
                    {r.explained_note && <div style={{ marginTop: 4, color: 'var(--success)' }}>✓ Explained: {r.explained_note} <span style={{ color: 'var(--muted)' }}>({r.explained_by}, {r.explained_at?.slice(0, 10)})</span></div>}
                    {explaining?.id === r.bank_payment_id && (
                      <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
                        <input autoFocus value={explaining.note} onChange={e => setExplaining(x => ({ ...x, note: e.target.value }))}
                          placeholder="e.g. paid to wrong payee, refunded 10-Feb-26" style={{ ...inputStyle, flex: 1 }} />
                        <button className="btn btn-primary" onClick={saveExplanation}>Save</button>
                        <button className="btn btn-secondary" onClick={() => setExplaining(null)}>Cancel</button>
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {r.document_id && <button className="btn btn-secondary" onClick={() => viewPdf(r.document_id)} title={r.source_report}>PDF</button>}
                    {canWrite && (r.flags.length > 0 || r.explained_note) && explaining?.id !== r.bank_payment_id && (
                      <button className="btn btn-secondary" style={{ marginLeft: 4 }} onClick={() => setExplaining({ id: r.bank_payment_id, note: r.explained_note || '' })}>
                        {r.explained_note ? 'Edit note' : 'Explain'}
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                <tr key={r.key}>
                  {canWrite && <td></td>}
                  <td>{r.flags.map(f => <div key={f}><Badge variant={FLAG_VARIANT[f]}>{f}</Badge></div>)}</td>
                  <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{r.payment_date}</td>
                  <td style={{ fontSize: 12 }}>
                    <div>AP payment #{r.ap_payment_id}</div>
                    <div style={{ color: 'var(--muted)', fontFamily: 'monospace' }}>{r.trace_id || '(no trace)'}</div>
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {r.vendor_name ? <EntityLink type="vendor" id={r.vendor_id}>{r.vendor_name}</EntityLink> : <EntityLink type="contractor" id={r.contractor_id}>{r.contractor_name}</EntityLink>}
                    {r.vendor_name && <div style={{ color: 'var(--muted)' }}>{r.contractor_name}</div>}
                  </td>
                  <td style={{ textAlign: 'right', color: 'var(--muted)' }}>—</td>
                  <td style={{ textAlign: 'right' }}>{fmt(r.ap_cash)}</td>
                  <td></td>
                  <td style={{ fontSize: 12 }}>
                    <EntityLink type="ap_invoice" id={r.ap_invoice_id}>{r.invoice_number}</EntityLink>
                    <span style={{ color: 'var(--muted)' }}> {r.client_wo_number}{r.po_number ? ` / ${r.po_number}` : ''}</span>
                    {(r.possible_bank || []).map(b => (
                      <div key={b.bank_payment_id} style={{ color: 'var(--warning)' }}>Possible: PNC {b.trace_id} ({b.payment_date})</div>
                    ))}
                    {(r.statement_proposals || []).map(s => (
                      <div key={s.id} style={{ color: 'var(--warning)' }}>Statement {s.date} {s.type} {s.payee || ''}{s.ref ? ` ref ${s.ref}` : ''} – {s.why}</div>
                    ))}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--muted)' }}>{r.note || ''}</td>
                  <td></td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length > shown && (
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <button className="btn btn-secondary" onClick={() => setShown(s => s + PAGE)}>Show {Math.min(PAGE, rows.length - shown)} more of {rows.length - shown}</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
